import { ButtonCus, ButtonSearch } from '@/components/common/Button';
import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem } from '@/components/common/Form';
import { InputWithClear } from '@/components/common/Input';
import TableActionButtons from '@/components/common/TableActionButtons';
import Table from '@/components/table/Table';
import { DEFAULT_SELECT_PAGE_SIZES, ekDomain, ROUTES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { LANGUAGES_SUPPORTED } from '@/types/enums';
import { Location } from '@/types/location';
import { yupResolver } from '@hookform/resolvers/yup';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import { CirclePlus } from 'lucide-react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { isPathEk } from '@/utils/routes';

// validate schema
const validateSchema = yup.object().shape({
    regionId: yup.string(),
    provinceId: yup.string(),
    name: yup.string().trim()
});

type FormData = yup.InferType<typeof validateSchema>;

export default observer(function LocationManagementPage() {
    // hook
    const { t, i18n: { language } } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isEk = isPathEk(pathname);

    // store
    const {
        locationStore: {
            locations, paging, totalRecord, defaultPaging, keepSearchParams,
            getLocations, setObservable
        },
        regionProvinceStore: {
            regions, provinces,
            getRegions, getProvinces, getProvincesByRegion
        },
        modalStore: { hideAllModals }
    } = useStore();

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: {
            regionId: keepSearchParams?.regionId || '',
            provinceId: keepSearchParams?.provinceId || '',
            name: keepSearchParams?.name || ''
        }
    });

    // state
    const [rowSelected, setRowSelected] = useState({});

    // lifecycle
    useEffect(() => {
        getRegions();
        // getProvinces();
        getProvinceByRegion(Number(keepSearchParams?.regionId || ''));

        // onSearch(keepSearchParams);
        getLocations(keepSearchParams ?? {}, paging);
    }, []);

    // function
    const onSearch = (searchParams: FormData) => {
        const params: any = { ...searchParams };
        getLocations(params, defaultPaging);
        searchParams && setObservable('keepSearchParams', toJS(searchParams));
        hideAllModals();
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging = stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        getLocations(keepSearchParams, newPaging);
    };

    // filter province when change region
    const getProvinceByRegion = (regionId: number | undefined) => {
        if (regionId) {
            getProvincesByRegion(regionId);
        } else {
            getProvinces();
        }
    };

    const onClickEdit = (locationId: number) => {
        if (isEk) {
            navigate(`/${ekDomain}${ROUTES.locationManagement.href}/${ROUTES.locationEdit.href}/${locationId}`);
        } else {
            navigate(`${ROUTES.locationManagement.href}/${ROUTES.locationEdit.href}/${locationId}`);
        }
    };

    // columns
    const columns: ColumnDef<Location>[] = [
        {
            id: 'id',
            accessorKey: 'id',
            header: '',
            size: 80,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <TableActionButtons
                    onView={() => onClickEdit(original.id)}
                />
            )
        },
        {
            accessorKey: 'image',
            // header: t('field.thumnail'),
            header: '',
            size: 200,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='min-w-[70px]'>
                    {(original?.attachEntry && original?.attachEntry?.length > 0) && (
                        <img
                            className='max-w-[70px] h-[50px] object-contain'
                            src={original?.attachEntry?.[0]?.path}
                            alt=''
                        />
                    )}
                </div>
            )
        },
        {
            accessorKey: 'name',
            header: t('field.location_name'),
            size: 400,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='min-w-[100px]'>
                    {original?.name || ''}
                </div>
            )
        },
        {
            accessorKey: 'region',
            header: t('field.region'),
            size: 400,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='min-w-[100px]'>
                    {language === LANGUAGES_SUPPORTED.Japanese ?
                        (original?.region?.nameJP || '') :
                        (original?.region?.name || '')}
                </div>
            )
        },
        {
            accessorKey: 'province',
            header: t('field.province'),
            size: 400,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='min-w-[100px]'>
                    {language === LANGUAGES_SUPPORTED.Japanese ?
                        (original?.province?.nameJP || '') :
                        (original?.province?.name || '')}
                </div>
            )
        },
        {
            accessorKey: 'pricing',
            header: t('field.pricing'),
            size: 400,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='min-w-[100px]'>
                    {original?.pricing ? original?.pricing.name : ''}
                </div>
            )
        }
    ];

    return (
        <>
            <div className='layout-card'>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSearch)}>
                        <div className='grid grid-cols-1 xsm:grid-cols-2 md:grid-cols-3 gap-6'>
                            <FormField
                                control={form.control}
                                name='regionId'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Combobox
                                                placeholder={t('field.region')}
                                                value={field.value ?? ''}
                                                onChange={(item) => {
                                                    field.onChange(item?.value ?? '');
                                                    form.setValue('regionId', item?.code ?? '');
                                                    form.setValue('provinceId', '');
                                                    getProvinceByRegion(item?.code);
                                                }}
                                                items={
                                                    (Array.isArray(regions) && regions?.length ?
                                                        [
                                                            ...regions
                                                                .filter(item => item.name)
                                                                .map(item => ({
                                                                    label: language === LANGUAGES_SUPPORTED.Japanese ? item.nameJP : item.name,
                                                                    value: item.id.toString(),
                                                                    code: item.id
                                                                }))
                                                        ] :
                                                        []
                                                    )
                                                }
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='provinceId'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Combobox
                                                placeholder={t('field.province')}
                                                value={field.value ?? ''}
                                                onChange={(item) => {
                                                    field.onChange(item?.value ?? '');
                                                    form.setValue('provinceId', item?.code ?? '');
                                                }}
                                                items={
                                                    (Array.isArray(provinces) && provinces?.length ?
                                                        [
                                                            ...provinces
                                                                .filter(item => item.name)
                                                                .map(item => ({
                                                                    label: language === LANGUAGES_SUPPORTED.Japanese ? item.nameJP : item.name,
                                                                    value: item.id.toString(),
                                                                    code: item.id
                                                                }))
                                                        ] :
                                                        []
                                                    )
                                                }
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='name'
                                render={({ field }) => (
                                    <FormItem className='col-span-full md:col-span-1'>
                                        <FormControl>
                                            <InputWithClear
                                                type='text'
                                                placeholder={t('field.location_name')}
                                                clear
                                                {...field}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <div className='col-span-full flex justify-center'>
                                <ButtonSearch size='md' className='max-w-52'>
                                    {t('button.search')}
                                </ButtonSearch>
                            </div>
                        </div>
                    </form>
                </Form>
            </div>
            <div className='layout-card mt-6'>
                <Table
                    columns={columns}
                    data={locations}
                    state={{
                        ...paging,
                        rowSelection: rowSelected
                    }}
                    rowCount={totalRecord}
                    onStateChange={onStateChange}
                    selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                    enableRowSelection={false}
                    onRowSelectionChange={setRowSelected}
                    getRowId={row => row.id?.toString()}
                    renderActions={(
                        <Link to={ROUTES.locationCreate.href}>
                            <ButtonCus variant='default' size='md'>
                                <CirclePlus className='mr-2' />
                                {t('button.add_new')}
                            </ButtonCus>
                        </Link>
                    )}
                />
            </div>
        </>
    );
});
